import { FC } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@components/ui/dialog.tsx";
import useTranslation from "@hooks/use_translation.ts";
import type { ProfileData } from "@/queries/use_profile.ts";
import type { SectionProps, SectionSource } from "./types.ts";
import ResumeSection from "./resume_section.tsx";
import AboutYouSection from "./about_you_section.tsx";
import ContactInfoSection from "./contact_info_section.tsx";
import EmploymentSection from "./employment_section.tsx";
import EducationSection from "./education_section.tsx";
import OnlineProfilesSection from "./online_profiles_section.tsx";
import SelfIdentificationSection from "./self_identification_section.tsx";
import SkillsSection from "./skills_section.tsx";
import WorkPreferencesSection from "./work_preferences_section.tsx";

type SectionConfig = {
  titleKey: string;
  descriptionKey?: string;
  Section: FC<SectionProps>;
};

const SECTIONS: Record<string, SectionConfig> = {
  resume: { titleKey: "resume.title", Section: ResumeSection },
  about_you: {
    titleKey: "about_you.title",
    descriptionKey: "about_you.description",
    Section: AboutYouSection,
  },
  contact_info: {
    titleKey: "contact_info.title",
    descriptionKey: "contact_info.description",
    Section: ContactInfoSection,
  },
  employment: {
    titleKey: "employment.title",
    descriptionKey: "employment.description",
    Section: EmploymentSection,
  },
  education: {
    titleKey: "education.title",
    descriptionKey: "education.description",
    Section: EducationSection,
  },
  online_profiles: {
    titleKey: "online_profiles.title",
    descriptionKey: "online_profiles.description",
    Section: OnlineProfilesSection,
  },
  voluntary_self_id: {
    titleKey: "voluntary_self_id.title",
    descriptionKey: "voluntary_self_id.description",
    Section: SelfIdentificationSection,
  },
  work_preferences: {
    titleKey: "work_preferences.title",
    descriptionKey: "work_preferences.description",
    Section: WorkPreferencesSection,
  },
  skills: {
    titleKey: "skills.title",
    descriptionKey: "skills.description",
    Section: SkillsSection,
  },
};

type Props = {
  openKey: string | null;
  onOpenChange: (open: boolean) => void;
  data: ProfileData;
  source?: SectionSource;
};

const ProfileSectionModal = ({ openKey, onOpenChange, data, source = "dashboard" }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_checklist.modals" });
  const section = openKey ? SECTIONS[openKey] : null;

  return (
    <Dialog open={openKey !== null} onOpenChange={onOpenChange}>
      <DialogContent hideClose={false} className="max-h-[85vh] overflow-y-auto">
        <DialogTitle>
          <div className="text-[18px] leading-[24px] font-semibold">
            {section ? t(section.titleKey) : ""}
          </div>
        </DialogTitle>
        {section?.descriptionKey && (
          <DialogDescription>
            <div>{t(section.descriptionKey)}</div>
          </DialogDescription>
        )}
        {section && (
          <section.Section data={data} onClose={() => onOpenChange(false)} source={source} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSectionModal;
